import { fmt, fmtDateShort, fmtQty } from '@k2pos/core';
import { Card } from '@/components/ui/Card';
import { Mono, Text } from '@/components/ui/Text';
import { colors } from '@/theme/tokens';
import styles from './InvoiceCard.module.css';

export interface InvoiceLine {
  productId: string;
  name: string;
  unit: string;
  qty: number;
  /** Line total in LAK. Already computed, so this component never does money maths. */
  amount: number;
  /** Unit price, when the caller has it. Omitted on a journal-sourced invoice. */
  price?: number;
}

interface InvoiceCardProps {
  /** Who issued it. On the bill itself, not only in the app bar. */
  shopName: string;
  shopPhone?: string;
  shopAddress?: string;
  invoiceNo: string;
  /** ISO-8601. */
  at: string;
  customerName?: string;
  credit?: boolean;
  lines: InvoiceLine[];
  total: number;
  /** Where to transfer the money. Shown only when the shop has set one. */
  bank?: string;
  /** The shop's bank QR as a data URL, from `useShopQr`. */
  qr?: string | null;
  footer?: string;
}

/**
 * The printed bill.
 *
 * Shared by the sale-done screen and the order history, so a bill looked up a
 * week later is the same document the customer was handed. The two used to be
 * one screen's markup, and duplicating it would have let them drift.
 *
 * It carries the shop's own name and the customer's because it has to stand
 * alone: `window.print()` is the browser fallback behind ພິມ, and the app bar
 * that used to supply both is chrome — it printed a back arrow onto a
 * customer's copy until it was hidden, and hiding it left the bill anonymous.
 */
export function InvoiceCard({
  shopName,
  shopPhone,
  shopAddress,
  invoiceNo,
  at,
  customerName,
  credit,
  lines,
  total,
  bank,
  qr,
  footer,
}: InvoiceCardProps) {
  return (
    <Card elevated className={styles.invoice}>
      <div className={styles.shop}>
        <Text weight={800} size={15} align="center">
          {shopName}
        </Text>
        {shopPhone || shopAddress ? (
          <Text size={10} color={colors.textMuted} align="center">
            {[shopPhone, shopAddress].filter(Boolean).join(' · ')}
          </Text>
        ) : null}
      </div>

      <div className={styles.head}>
        <Mono weight={700} size={12} color={colors.textMuted} className="selectable">
          ໃບບິນ #{invoiceNo}
        </Mono>
        <Mono size={12} color={colors.textMuted}>
          {fmtDateShort(new Date(at))}
        </Mono>
      </div>

      {customerName ? (
        <div className={styles.head}>
          <Text size={11} color={colors.textMuted} lines={1}>
            {customerName}
          </Text>
          <Text weight={700} size={11} color={credit ? colors.skyDeep : colors.navy}>
            {credit ? 'ຕິດໜີ້' : 'ຈ່າຍເລີຍ'}
          </Text>
        </div>
      ) : null}

      {lines.map((line, i) => (
        <div key={`${line.productId}-${line.unit}`}>
          {i > 0 ? <div className={styles.dash} /> : null}
          <div className={styles.line}>
            <div className="grow col">
              <Text size={12.5} lines={1}>
                {line.name}
              </Text>
              <Mono size={10.5} color={colors.textFaint}>
                {fmtQty(line.qty)} {line.unit}
                {line.price === undefined ? '' : ` × ${fmt(line.price)}`}
              </Mono>
            </div>
            <Mono size={12.5} className="selectable">
              {fmt(line.amount)}
            </Mono>
          </div>
        </div>
      ))}

      <div className={styles.total}>
        <Text weight={800} size={14}>
          ລວມ
        </Text>
        <Mono weight={700} size={17} className="selectable">
          {fmt(total)} ₭
        </Mono>
      </div>

      {/* The account to transfer into, on the bill itself rather than on the
          sale-done screen: a customer who is going to transfer does it from the
          piece of paper — or the shared photo of it — that they walked out
          with, not from the shopkeeper's phone. Mono and selectable, because
          the next thing anyone does with an account number is copy it. */}
      {bank || qr ? (
        <div className={styles.bank}>
          <Text size={9.5} weight={700} color={colors.textMuted}>
            ໂອນເຂົ້າບັນຊີ
          </Text>
          {/* The shop's own QR, as uploaded. Nothing is generated from it and
              nothing overlays the amount on it — a scanner that finds two codes
              in one image reads whichever it likes, so a "helpful" badge on top
              is a wrong transfer waiting to happen. */}
          {qr ? <img src={qr} alt="QR ໂອນເງິນ" className={styles.qr} /> : null}
          {bank ? (
            <Mono size={11.5} className="selectable">
              {bank}
            </Mono>
          ) : null}
        </div>
      ) : null}

      {footer ? (
        <Text size={10.5} color={colors.textFaint} align="center" className={styles.footer}>
          {footer}
        </Text>
      ) : null}
    </Card>
  );
}
